// ─── Welcome Connect Page ───────────────────────────────────────
// 3-step flow for connecting an existing GitHub repo:
//   Step 1: Select repo from the tenant's own GitHub App install (/subscriber/repos)
//   Step 2: Confirm project name + display name
//   Step 3: Start initial sync (redirects to wizard)
//
// Full-viewport, no sidebar. Back button returns to Welcome.
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { ArrowLeft, ArrowRight, Check, GitBranch, Loader2, Search, AlertCircle } from 'lucide-react';
import api, { getSubscriberRepos } from '../api';
import type { GitHubRepo } from '../api';

interface WelcomeConnectProps {
  onBack: () => void;
  onComplete: (projectId: string) => void;
  registeredProjects: Array<{ project_id: string }>;
}

export default function WelcomeConnect({ onBack, onComplete, registeredProjects }: WelcomeConnectProps) {
  const [step, setStep] = useState(1);
  const [repos, setRepos] = useState<GitHubRepo[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [selectedRepo, setSelectedRepo] = useState<GitHubRepo | null>(null);
  const [projectId, setProjectId] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [creating, setCreating] = useState(false);

  // Fetch repos on mount
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        setError(null);
        // Punch 95735b3a: the lumeniqjs org listing is service-only (403 for
        // subscribers). Read only the caller tenant's own installation.
        const res = await getSubscriberRepos();
        const names = Array.isArray(res?.repos) ? res.repos : [];
        if (!cancelled) {
          setRepos(names.map((fullName: string) => ({
            full_name: fullName,
            name: fullName.includes('/') ? fullName.slice(fullName.indexOf('/') + 1) : fullName,
            description: null,
            language: null,
          }) as GitHubRepo));
        }
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : 'Failed to load repositories');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Filter repos — exclude already-registered ones
  const registeredIds = useMemo(() => new Set(registeredProjects.map((p: { project_id: string }) => p.project_id)), [registeredProjects]);
  const filteredRepos = useMemo(() => {
    return repos
      .filter((r: GitHubRepo) => !registeredIds.has(r.name))
      .filter((r: GitHubRepo) => {
        if (!search.trim()) return true;
        const q = search.toLowerCase();
        return r.name.toLowerCase().includes(q) || (r.description || '').toLowerCase().includes(q);
      })
      .sort((a: GitHubRepo, b: GitHubRepo) => a.full_name.localeCompare(b.full_name));
  }, [repos, registeredIds, search]);

  // Auto-derive project ID from repo name
  const handleSelectRepo = useCallback((repo: GitHubRepo) => {
    setSelectedRepo(repo);
    setProjectId(repo.name.toLowerCase().replace(/[^a-z0-9-]/g, '-'));
    setDisplayName(repo.name.replace(/[-_]/g, ' ').replace(/\b\w/g, (c: string) => c.toUpperCase()));
    setStep(2);
  }, []);

  // Create project and start initial sync
  const handleConfirm = useCallback(async () => {
    if (!selectedRepo || !projectId.trim() || !displayName.trim()) return;
    setCreating(true);
    setError(null);
    try {
      // Register the project via API — uses wizardStart which creates the project
      // Pass repo info so the backend can register it in the in-memory registry
      const [repoOwner] = selectedRepo.full_name.split('/');
      const created=await api.wizardStart({
        name: projectId,
        description: displayName,
        repo_owner: repoOwner,
        repo_name: selectedRepo.name,
        default_branch: selectedRepo.default_branch || 'main',
      });
      const connectedProjectId=created.projectId;
      window.dispatchEvent(new Event('ocn:projects-changed'));
      // Trigger initial sync so phases stream into Chat (AC #21)
      try {
        await api.syncRun(connectedProjectId, 'onboarding');
      } catch (syncErr) {
        // Show sync error inline — do NOT navigate to Chat (AC #21)
        const syncMsg = syncErr instanceof Error ? syncErr.message : 'Failed to start sync';
        setError(`Project created, but sync failed: ${syncMsg}. You can retry from the project page.`);
        setCreating(false);
        // Still navigate to Chat after a longer delay — project exists, sync can be retried
        setTimeout(() => onComplete(connectedProjectId), 3000);
        return;
      }
      setStep(3);
      // Brief pause to show success, then redirect to Chat
      setTimeout(() => onComplete(connectedProjectId), 1500);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create project');
      setCreating(false);
    }
  }, [selectedRepo, projectId, displayName, onComplete]);

  const stepIndicator = (
    <div style={{
      display: 'flex', alignItems: 'center', gap: 8,
      marginBottom: 32, justifyContent: 'center',
    }}>
      {[1, 2, 3].map((s: number) => (
        <React.Fragment key={s}>
          <div style={{
            width: 28, height: 28, borderRadius: '50%',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: 12, fontWeight: 600,
            background: step >= s ? 'var(--blue)' : 'var(--surface)',
            color: step >= s ? '#fff' : 'var(--text-dim)',
            border: `1px solid ${step >= s ? 'var(--blue)' : 'var(--border)'}`,
            transition: 'all 0.2s',
          }}>
            {step > s ? <Check size={14} /> : s}
          </div>
          {s < 3 && (
            <div style={{
              width: 40, height: 1,
              background: step > s ? 'var(--blue)' : 'var(--border)',
              transition: 'background 0.2s',
            }} />
          )}
        </React.Fragment>
      ))}
    </div>
  );

  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 1000,
      background: 'var(--bg)',
      display: 'flex', flexDirection: 'column',
      alignItems: 'center',
      fontFamily: 'var(--font-mono)',
      color: 'var(--text)',
      overflow: 'auto',
    }}>
      {/* Header */}
      <div style={{
        width: '100%', padding: '20px 32px',
        display: 'flex', alignItems: 'center', gap: 10,
      }}>
        <button
          onClick={step === 1 ? onBack : () => setStep((s: number) => Math.max(1, s - 1))}
          style={{
            background: 'none', border: 'none', cursor: 'pointer',
            color: 'var(--text-dim)', display: 'flex', alignItems: 'center', gap: 6,
            fontSize: 'var(--text-xs)', fontFamily: 'inherit',
          }}
        >
          <ArrowLeft size={14} />
          <span>{step === 1 ? 'Back to welcome' : 'Back'}</span>
        </button>
      </div>

      {/* Content */}
      <div style={{ maxWidth: 560, width: '100%', padding: '0 32px', marginTop: 40 }}>
        {stepIndicator}

        {/* Step 1: Select repo */}
        {step === 1 && (
          <>
            <h2 style={{
              fontSize: 20, fontWeight: 700, textAlign: 'center', marginBottom: 4,
              fontFamily: 'var(--font-sans, Syne, sans-serif)',
            }}>
              Select a repository
            </h2>
            <p style={{
              fontSize: 'var(--text-xs)', color: 'var(--text-dim)',
              textAlign: 'center', marginBottom: 20,
            }}>
              Choose a repository you granted OCNexus access to on GitHub.
            </p>

            {/* Search */}
            <div style={{
              position: 'relative', marginBottom: 16,
            }}>
              <Search size={14} style={{
                position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)',
                color: 'var(--text-dim)',
              }} />
              <input
                type="text"
                value={search}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSearch(e.target.value)}
                placeholder="Filter repositories..."
                style={{
                  width: '100%', padding: '10px 12px 10px 34px',
                  background: 'var(--surface)', border: '1px solid var(--border)',
                  borderRadius: 8, color: 'var(--text)',
                  fontSize: 'var(--text-sm)', fontFamily: 'inherit',
                  outline: 'none',
                }}
              />
            </div>

            {/* Repo list */}
            {loading && (
              <div style={{ textAlign: 'center', padding: 40, color: 'var(--text-dim)' }}>
                <Loader2 size={20} style={{ animation: 'spin 1s linear infinite' }} />
                <div style={{ marginTop: 8, fontSize: 'var(--text-xs)' }}>Loading repositories...</div>
              </div>
            )}
            {error && (
              <div style={{
                padding: 16, borderRadius: 8,
                background: 'rgba(248, 81, 73, 0.1)', border: '1px solid rgba(248, 81, 73, 0.3)',
                display: 'flex', alignItems: 'center', gap: 8,
                fontSize: 'var(--text-xs)', color: 'var(--red, #f85149)',
              }}>
                <AlertCircle size={14} />
                <span>{error}</span>
              </div>
            )}
            {!loading && !error && (
              <div style={{
                maxHeight: 380, overflowY: 'auto',
                border: '1px solid var(--border)', borderRadius: 8,
              }}>
                {filteredRepos.length === 0 ? (
                  <div style={{
                    padding: 32, textAlign: 'center',
                    fontSize: 'var(--text-xs)', color: 'var(--text-dim)',
                  }}>
                    {search ? 'No matching repositories' : 'No unregistered repositories found'}
                  </div>
                ) : (
                  filteredRepos.map((repo: GitHubRepo, i: number) => (
                    <button
                      key={repo.name}
                      onClick={() => handleSelectRepo(repo)}
                      style={{
                        width: '100%', padding: '12px 16px',
                        background: 'transparent',
                        borderTop: i > 0 ? '1px solid var(--border)' : 'none',
                        borderBottom: 'none', borderLeft: 'none', borderRight: 'none',
                        cursor: 'pointer', textAlign: 'left',
                        display: 'flex', alignItems: 'center', gap: 12,
                        color: 'var(--text)', fontFamily: 'inherit',
                        transition: 'background 0.1s',
                      }}
                      onMouseEnter={(e: React.MouseEvent<HTMLButtonElement>) => { e.currentTarget.style.background = 'var(--surface)'; }}
                      onMouseLeave={(e: React.MouseEvent<HTMLButtonElement>) => { e.currentTarget.style.background = 'transparent'; }}
                    >
                      <GitBranch size={16} style={{ color: 'var(--text-dim)', flexShrink: 0 }} />
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: 'var(--text-sm)', fontWeight: 500 }}>
                          {repo.name}
                        </div>
                        {repo.description && (
                          <div style={{
                            fontSize: 'var(--text-xs)', color: 'var(--text-dim)',
                            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                          }}>
                            {repo.description}
                          </div>
                        )}
                      </div>
                      {repo.language && (
                        <span style={{
                          fontSize: 10, color: 'var(--text-dim)',
                          padding: '2px 6px', background: 'var(--surface)',
                          borderRadius: 4, flexShrink: 0,
                        }}>
                          {repo.language}
                        </span>
                      )}
                      <ArrowRight size={14} style={{ color: 'var(--text-dim)', flexShrink: 0 }} />
                    </button>
                  ))
                )}
              </div>
            )}
          </>
        )}

        {/* Step 2: Confirm project */}
        {step === 2 && selectedRepo && (
          <>
            <h2 style={{
              fontSize: 20, fontWeight: 700, textAlign: 'center', marginBottom: 4,
              fontFamily: 'var(--font-sans, Syne, sans-serif)',
            }}>
              Confirm project details
            </h2>
            <p style={{
              fontSize: 'var(--text-xs)', color: 'var(--text-dim)',
              textAlign: 'center', marginBottom: 24,
            }}>
              Review and customize your project settings.
            </p>

            <div style={{
              background: 'var(--surface)', border: '1px solid var(--border)',
              borderRadius: 10, padding: 24,
              display: 'flex', flexDirection: 'column', gap: 20,
            }}>
              {/* Repo info */}
              <div style={{
                display: 'flex', alignItems: 'center', gap: 10,
                padding: '12px 16px', background: 'var(--bg)', borderRadius: 8,
              }}>
                <GitBranch size={16} style={{ color: 'var(--blue)' }} />
                <div>
                  <div style={{ fontSize: 'var(--text-sm)', fontWeight: 500 }}>
                    {selectedRepo.full_name}
                  </div>
                  {selectedRepo.description && (
                    <div style={{ fontSize: 'var(--text-xs)', color: 'var(--text-dim)' }}>
                      {selectedRepo.description}
                    </div>
                  )}
                </div>
              </div>

              {/* Project ID */}
              <div>
                <label style={{
                  display: 'block', fontSize: 'var(--text-xs)',
                  fontWeight: 500, marginBottom: 6, color: 'var(--text-dim)',
                }}>
                  Project ID
                </label>
                <input
                  type="text"
                  value={projectId}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => setProjectId(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '-'))}
                  style={{
                    width: '100%', padding: '10px 12px',
                    background: 'var(--bg)', border: '1px solid var(--border)',
                    borderRadius: 6, color: 'var(--text)',
                    fontSize: 'var(--text-sm)', fontFamily: 'inherit',
                    outline: 'none',
                  }}
                />
                <div style={{ fontSize: 10, color: 'var(--text-dim)', marginTop: 4 }}>
                  Lowercase, hyphens only. Used in API calls and config files.
                </div>
              </div>

              {/* Display Name */}
              <div>
                <label style={{
                  display: 'block', fontSize: 'var(--text-xs)',
                  fontWeight: 500, marginBottom: 6, color: 'var(--text-dim)',
                }}>
                  Display Name
                </label>
                <input
                  type="text"
                  value={displayName}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => setDisplayName(e.target.value)}
                  style={{
                    width: '100%', padding: '10px 12px',
                    background: 'var(--bg)', border: '1px solid var(--border)',
                    borderRadius: 6, color: 'var(--text)',
                    fontSize: 'var(--text-sm)', fontFamily: 'inherit',
                    outline: 'none',
                  }}
                />
              </div>

              {error && (
                <div style={{
                  padding: 12, borderRadius: 6,
                  background: 'rgba(248, 81, 73, 0.1)', border: '1px solid rgba(248, 81, 73, 0.3)',
                  fontSize: 'var(--text-xs)', color: 'var(--red, #f85149)',
                  display: 'flex', alignItems: 'center', gap: 8,
                }}>
                  <AlertCircle size={14} />
                  <span>{error}</span>
                </div>
              )}

              {/* Confirm button */}
              <button
                onClick={handleConfirm}
                disabled={creating || !projectId.trim() || !displayName.trim()}
                style={{
                  width: '100%', padding: '12px',
                  background: creating ? 'var(--text-dim)' : 'var(--blue)',
                  color: '#fff', border: 'none', borderRadius: 8,
                  fontSize: 'var(--text-sm)', fontWeight: 600,
                  cursor: creating ? 'not-allowed' : 'pointer',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                  fontFamily: 'inherit',
                  opacity: (!projectId.trim() || !displayName.trim()) ? 0.5 : 1,
                }}
              >
                {creating ? (
                  <>
                    <Loader2 size={14} style={{ animation: 'spin 1s linear infinite' }} />
                    Creating project...
                  </>
                ) : (
                  <>
                    Create &amp; start setup
                    <ArrowRight size={14} />
                  </>
                )}
              </button>
            </div>
          </>
        )}

        {/* Step 3: Success */}
        {step === 3 && (
          <div style={{ textAlign: 'center', padding: '40px 0' }}>
            <div style={{
              width: 56, height: 56, borderRadius: '50%',
              background: 'rgba(63, 185, 80, 0.15)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              margin: '0 auto 16px',
            }}>
              <Check size={28} style={{ color: 'var(--green)' }} />
            </div>
            <h2 style={{
              fontSize: 20, fontWeight: 700, marginBottom: 8,
              fontFamily: 'var(--font-sans, Syne, sans-serif)',
            }}>
              Project created
            </h2>
            <p style={{
              fontSize: 'var(--text-xs)', color: 'var(--text-dim)',
              marginBottom: 16,
            }}>
              Starting initial sync &mdash; opening Chat...
            </p>
            <Loader2 size={16} style={{
              color: 'var(--blue)',
              animation: 'spin 1s linear infinite',
            }} />
          </div>
        )}
      </div>
    </div>
  );
}
